import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Skeleton,
  Stack,
  SxProps,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DateTime } from "luxon";
import React, { useMemo, useState } from "react";

export type CalendarEvent = {
  id: string;
  title: string;
  /** ISO date (YYYY-MM-DD) */
  deadline: string;
};

export type MonthCalendarProps<T extends CalendarEvent = CalendarEvent> = {
  loading?: boolean;
  sx?: SxProps;
  /** Events for (at least) the currently visible matrix range */
  events: T[];
  /** Initial visible month; defaults to today */
  initialMonth?: DateTime;
  /** 0=Sunday, 1=Monday ... */
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  /** Click handler for a single instance */
  onEventClick?: (ev: T) => void;
  /** Max visible event chips per day before "+n more" */
  maxPerDay?: number;
  /** Optional: render a custom chip */
  renderEventChip?: (ev: T, truncateText?: boolean) => React.ReactNode;
  /** Height of the calendar (auto adjusts rows); defaults to 640 */
  height?: number;
  /** Optional: handler for month change */
  onMonthChange?: (month: DateTime) => void;
};

export default function Calendar<T extends CalendarEvent = CalendarEvent>({
  loading,
  events,
  initialMonth,
  weekStartsOn = 0,
  renderEventChip,
  onEventClick,
  maxPerDay = 3,
  height = 864,
  onMonthChange,
  sx,
}: MonthCalendarProps<T>) {
  const theme = useTheme();
  const [viewMonth, setViewMonth] = useState<DateTime>(
    initialMonth ?? DateTime.local()
  );

  const { weeks } = useMemo(
    () => buildMonthMatrix(viewMonth, weekStartsOn),
    [viewMonth, weekStartsOn]
  );

  const flatDays = useMemo(() => weeks.flat(), [weeks]);
  const eventsByDay = useMemo(
    () => mapEventsByDay<T>(events, flatDays),
    [events, flatDays]
  );

  // Popover state for "+n more"
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [moreForDate, setMoreForDate] = useState<DateTime | null>(null);

  const openMore = Boolean(anchorEl && moreForDate);
  const handleMoreClick = (e: React.MouseEvent<HTMLElement>, d: DateTime) => {
    setAnchorEl(e.currentTarget);
    setMoreForDate(d);
  };
  const handleMoreClose = () => {
    setAnchorEl(null);
    setMoreForDate(null);
  };

  const monthTitle = viewMonth.toFormat("LLLL yyyy"); // e.g., November 2025

  // Weekday headers
  const weekdayLabels = useMemo(() => {
    const labels: string[] = [];
    for (let i = 0; i < 7; i++) {
      const w = (weekStartsOn + i) % 7; // 0..6 where 0=Sun
      const luxonWeekday = ((w + 6) % 7) + 1; // map to Luxon 1=Mon..7=Sun
      const d = DateTime.local().set({ weekday: luxonWeekday });
      labels.push(d.toFormat("cccc"));
    }
    return labels;
  }, [weekStartsOn]);

  return (
    <Box sx={sx}>
      {/* Header */}
      <Stack direction="row" alignItems="center" sx={{ px: 1, py: 1.25 }}>
        <Stack direction="row" spacing={0.5} paddingRight={1}>
          <IconButton
            aria-label="Previous month"
            size="small"
            onClick={() => {
              const previousMonth = viewMonth.minus({ months: 1 });
              setViewMonth(previousMonth);
              onMonthChange?.(previousMonth);
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            aria-label="Next month"
            size="small"
            onClick={() => {
              const nextMonth = viewMonth.plus({ months: 1 });
              setViewMonth(nextMonth);
              onMonthChange?.(nextMonth);
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Stack>
        <Typography variant="h6">{monthTitle}</Typography>
        <Box width={48} />
      </Stack>

      {/* Weekday row */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          border: `1px solid ${theme.palette.divider}`,
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
        }}
      >
        {weekdayLabels.map((label, idx) => (
          <Box
            key={idx}
            sx={{
              p: 1,
              textAlign: "center",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textTransform: "uppercase" }}
            >
              {label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Month grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          height,
          borderLeft: `1px solid ${theme.palette.divider}`,
        }}
      >
        {weeks.map((week, wi) => (
          <React.Fragment key={wi}>
            {week.map((day) => {
              const iso = day.toISODate();
              if (!iso) return null;
              const inMonth = day.hasSame(viewMonth, "month");
              const isToday = day.hasSame(DateTime.local(), "day");
              const dayEvents = eventsByDay.get(iso) || [];
              const visible = dayEvents.slice(0, maxPerDay);
              const overflow = Math.max(0, dayEvents.length - visible.length);

              return (
                <Box
                  key={iso}
                  sx={{
                    borderRight: `1px solid ${theme.palette.divider}`,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    height: height / 6,
                    p: 0.75,
                    bgcolor: isToday
                      ? theme.palette.action.selected
                      : undefined,
                  }}
                >
                  {/* Day number */}
                  <Box display="flex" justifyContent="center" sx={{ mb: 0.5 }}>
                    <Typography
                      variant="body2"
                      color={inMonth ? "text.primary" : "text.disabled"}
                    >
                      {day.day === 1 ? day.toFormat("LLL d") : day.day}
                    </Typography>
                  </Box>

                  {/* Events */}
                  <Stack spacing={0.5}>
                    {loading ? (
                      <Skeleton variant="text" />
                    ) : (
                      <>
                        {visible.map((ev) =>
                          renderEventChip ? (
                            <span
                              key={ev.id + iso}
                              onClick={() => onEventClick?.(ev)}
                            >
                              {renderEventChip(ev)}
                            </span>
                          ) : (
                            <Tooltip key={ev.id + iso} title={ev.title} arrow>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {ev.title}
                              </Typography>
                            </Tooltip>
                          )
                        )}
                        {overflow > 0 && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ cursor: "pointer" }}
                            onClick={(e) => handleMoreClick(e as any, day)}
                          >
                            +{overflow} more
                          </Typography>
                        )}
                      </>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </React.Fragment>
        ))}
      </Box>

      {/* More popover */}
      <Popover
        open={openMore}
        anchorEl={anchorEl}
        onClose={handleMoreClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: { sx: { borderRadius: 2 } },
        }}
      >
        <Box sx={{ width: 200 }}>
          <Box sx={{ px: 1, py: 1, position: "relative" }}>
            <IconButton
              aria-label="Close"
              size="small"
              onClick={handleMoreClose}
              sx={{ position: "absolute", top: 4, right: 4 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.25,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: "uppercase" }}
              >
                {moreForDate?.toFormat("cccc")}
              </Typography>
              <Typography variant="body2">
                {moreForDate?.toFormat("d")}
              </Typography>
            </Box>
          </Box>
          <List dense>
            {(moreForDate
              ? eventsByDay.get(moreForDate.toISODate() || "") || []
              : []
            ).map((ev) =>
              renderEventChip ? (
                <ListItemButton
                  key={ev.id}
                  onClick={() => {
                    onEventClick?.(ev);
                    handleMoreClose();
                  }}
                >
                  {renderEventChip(ev, false)}
                </ListItemButton>
              ) : (
                <ListItemButton
                  key={ev.id}
                  onClick={() => {
                    onEventClick?.(ev);
                    handleMoreClose();
                  }}
                >
                  <ListItemText
                    primary={ev.title}
                    slotProps={{
                      primary: {
                        noWrap: true,
                      },
                    }}
                  />
                </ListItemButton>
              )
            )}
          </List>
        </Box>
      </Popover>
    </Box>
  );
}

/**
 * Build a 6x7 matrix of DateTimes covering the month grid, padded by leading/trailing days.
 */
function buildMonthMatrix(view: DateTime, weekStartsOn: number) {
  const startOfMonth = view.startOf("month");
  const endOfMonth = view.endOf("month");

  // Compute the grid start/end inclusive
  let gridStart = startOfMonth.startOf("week");
  let gridEnd = endOfMonth.endOf("week");

  // Adjust for custom week start (Luxon uses locale; we force using weekday)
  const normalizeToWeekStart = (d: DateTime) => {
    const diff = (d.weekday % 7) - weekStartsOn; // luxon: 1=Mon .. 7=Sun; normalize to 0..6 via %7
    const shift = diff < 0 ? 7 + diff : diff; // days since desired week start
    return d.minus({ days: shift });
  };
  const normalizeToWeekEnd = (d: DateTime) =>
    normalizeToWeekStart(d).plus({ days: 6 }).endOf("day");

  gridStart = normalizeToWeekStart(startOfMonth.startOf("day"));
  gridEnd = normalizeToWeekEnd(endOfMonth.endOf("day"));

  // Ensure exactly 6 weeks (common UI expectation)
  const days = [] as DateTime[];
  const total = 6 * 7;
  for (let i = 0; i < total; i++) days.push(gridStart.plus({ days: i }));
  const weeks: DateTime[][] = Array.from({ length: 6 }, (_, i) =>
    days.slice(i * 7, (i + 1) * 7)
  );

  return { weeks, gridStart, gridEnd };
}

/**
 * Group events by day for efficient rendering.
 * Assumes events are single-day deadlines (no multi-day spans).
 */
function mapEventsByDay<T extends CalendarEvent>(
  events: T[],
  days: DateTime[]
): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const d of days) {
    const iso = d.toISODate();
    if (iso) map.set(iso, []);
  }
  for (const ev of events) {
    const iso = DateTime.fromISO(ev.deadline).toISODate();
    if (iso && map.has(iso)) {
      const bucket = map.get(iso);
      if (bucket) bucket.push(ev);
    }
  }
  return map;
}
